import { createBrowserHistory } from 'history';
import { createElement } from 'react';
import { hydrate, render } from 'react-dom';
import Store from 'repatch';
import HttpClient from './adapter/HttpClient';
import GraphQLClient from './adapter/GraphQLClient';
import initApolloClient, { LinkArgs } from './adapter/apolloClient';
import KaizenFilesClient from './adapter/KaizenFilesClient';
import Config from './core/Config';
import State from './core/State';
import setLocation from './thunk/setLocation';
import baseIntiialState from './initialState';
import ErrorPresenter from './service/ErrorPresenter';
import ErrorReporter from './service/ErrorReporter';
import MessageDispatcher from './service/MessageDispatcher';
import AuthUserWebApi from './service/persistent/AuthUserWebApi';
import OrganizationWebApi from './service/persistent/OrganizationWebApi';
import OrganizationWebApiId from './service/persistent/OrganizationWebApiId';
import AuthenticationUsecase from './usecase/AuthenticationUsecase';
import createKukerEmitterMiddleware from './kukerEmitterMiddleware';
import Root from './view/Root';
import EditGlobalChartSettingUsecase from './usecase/EditGlobalChartSettingUsecase';
import OrganizationUsecase from './usecase/OrganizationUsecase';
import CreateChartsUsecase from './usecase/CreateChartsUsecase';
import SalesWebApi from './service/persistent/SalesWebApi';
import SummariesWebApi from './service/persistent/SummariesWebApi';
import VisitorWebApi from './service/persistent/VisitorWebApi';
import SegmentWebApi from './service/persistent/SegmentWebApi';
import CreateTableUsecase from './usecase/CreateTableUsecase';
import ApiErrorHandler from './service/ApiErrorHandler';
import LiftReportWebApi from './service/persistent/LiftReportWebApi';
import LiftReportUsecase from './usecase/LiftReportUsecase';
import AuthenticationWebApi from './service/persistent/AuthenticationWebApi';
import GoogleAnalytics from './adapter/GoogleAnalytics';
import PerformanceMeasure from './service/PerformanceMeasure';
import CustomReportWebApi from './service/persistent/CustomReportWebApi';
import CustomReportUsecase from './usecase/CustomReportUsecase';
import PreviewCommentUsecase from './usecase/PreviewCommentUsecase';
import PreviewCommentWebApi from './service/persistent/PreviewCommentWebApi';
import JavaScriptCodeWebApi from './service/persistent/JavaScriptCodeWebApi';
import JavaScriptCodeUsecase from './usecase/JavaScriptCodeUsecase';
import GoogleOauthService from './service/GoogleOauthService';
import hasOnlyProperty from './utility/hasOnlyProperty';
import { isArray } from '@apollo/client/cache/inmemory/helpers';

const main = () => {
  const config: Config = (window as any).__hugotconfig__;
  const appVersion: string = (window as any).__hugotversion__;

  const initialState = {
    ...baseIntiialState,
    location: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
  };

  const history = createBrowserHistory({ window });

  const store = new Store<State>(initialState);

  // Adapter
  const httpIdClient = new HttpClient({
    baseUrl: config.CORE_API_BASE_URL,
  });

  const httpClient = new HttpClient({
    baseUrl: config.LIFT_REPORT_API_BASE,
  });

  const graphqlClient = new GraphQLClient({
    endpointUrl: config.INTELLIGENCE_API_BASE,
  });

  const idGraphqlClient = new GraphQLClient({
    endpointUrl: config.ID_API_BASE,
  });

  const liftReportGraphqlClient = new GraphQLClient({
    endpointUrl: `${config.LIFT_REPORT_API_BASE}/graphql`,
    isLiftReport: true,
  });

  const dataPlatformGraphqlClient = new GraphQLClient({
    endpointUrl: config.DATA_PLATFORM_API_BASE,
  });

  const apiConfig: LinkArgs = {
    liftreport: {
      // first one is default
      type: 'gql',
      uri: `${config.LIFT_REPORT_API_BASE}/graphql`,
      credentials: 'include',
      headers: { Authorization: 'Basic ZGV2OmthaXplbjUwMg==' },
      useCustomFetch: false,
    },
    planbcd: {
      type: 'rest',
      uri: `${config.LIFT_REPORT_API_BASE}`,
      credentials: 'include',
      headers: { Authorization: 'Basic ZGV2OmthaXplbjUwMg==' },
      responseTransformer: async (response: Response) => {
        if (response) {
          if (!response?.status) return null;
          return response.json().then((data: { data: any; meta?: any; [key: string]: any }) => {
            if (hasOnlyProperty(data, 'data')) {
              return data.data;
            } else {
              // since Apollo expects a {Type} object or array of {Type} as root object,
              // other fields from REST need to be vested into the {Type}
              const { data: _data, ...otherFields } = data;
              const vestedObject =
                isArray(_data) &&
                _data.map((obj) => {
                  return {
                    ...obj,
                    ...otherFields,
                  };
                });

              return vestedObject || { ..._data, ...otherFields };
            }
          });
        }
        throw new Error('rest api response is null');
      },
      bodySerializers: {
        // TODO: REST serializers depend on API spec. Better to be configured somewhere else and imported
        fileSerializer: <Input, Headers>(input: Input, headers: Headers) => {
          //@ts-ignore
          const { data } = input as PostAttachmentInput;
          const formData = new FormData();
          formData.append('attachment[description]', '');
          formData.append('attachment[file]', data as Blob);
          return { body: formData, headers };
        },
        instantImageSerializer: <Input, Headers>(input: Input, headers: Headers) => {
          //@ts-ignore
          const { data, submit_comment } = input as ChangeInstantImageInput;
          const formData = new FormData();
          formData.append('variation[attachments_attributes[0][file]]', data as Blob);
          formData.append('variation[submit_comment]', submit_comment);
          return { body: formData, headers };
        },
        screenshotSerializer: <Input, Headers>(input: Input, headers: Headers) => {
          //@ts-ignore
          const { data } = input as ChangeInstantImageInput;
          const formData = new FormData();
          formData.append('variation[image]', data as Blob);
          return { body: formData, headers };
        },
      },
    },
    search_console: {
      type: 'rest',
      uri: `${config.GOOGLE_COLLECTOR_API_BASE_URL}`,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      responseTransformer: async (response: Response) => {
        if (response) return response.json().then(({ data }: { data: any }) => data);
        throw new Error('rest api response is null');
      },
    },
    id: {
      type: 'gql',
      uri: `${config.ID_API_BASE}`,
      credentials: 'include',
      useCustomFetch: true,
    },
    dataplatform: {
      type: 'gql',
      uri: `${config.DATA_PLATFORM_API_BASE}`,
      credentials: 'include',
      useCustomFetch: true,
    },
  };
  const apolloClient = initApolloClient(apiConfig);

  const kaizenFilesClient = new KaizenFilesClient({ baseUrl: config.FILES_API });

  const googleAnalytics = new GoogleAnalytics((window as any).gtag, config.GA_TRACKING_ID);
  const performanceMeasure = new PerformanceMeasure({ googleAnalytics });

  // Service
  const errorPresenter = new ErrorPresenter({ store });

  const errorReporter = new ErrorReporter({
    store,
    dsn: config.SENTRY_API_DSN,
    environment: config.STAGE,
    release: appVersion,
  });

  const authenticationWebApi = new AuthenticationWebApi({ httpClient: httpIdClient });

  const messageDispatcher = new MessageDispatcher({ store });

  const authUserWebApi = new AuthUserWebApi({ idGraphqlClient });

  const organizationWebApi = new OrganizationWebApi({ graphqlClient });

  const organizationWebApiId = new OrganizationWebApiId({ idGraphqlClient });

  const salesWebApi = new SalesWebApi({ graphqlClient });

  const summariesWebApi = new SummariesWebApi({ graphqlClient });

  const visitorWebApi = new VisitorWebApi({ graphqlClient });

  const segmentWebApi = new SegmentWebApi({ graphqlClient });

  const javaScriptCodeWebApi = new JavaScriptCodeWebApi({ httpClient });

  const apiErrorHandler = new ApiErrorHandler({ errorPresenter, errorReporter });

  const liftReportWebApi = new LiftReportWebApi({ liftReportGraphqlClient, dataPlatformGraphqlClient });

  const customReportWebApi = new CustomReportWebApi({ graphqlClient });

  const previewCommentWebApi = new PreviewCommentWebApi({ liftReportGraphqlClient, kaizenFilesClient });

  const googleOauthService = new GoogleOauthService({
    resolveGapi: () => (window as any).gapi,
    clientId: config.GOOGLE_AUTH_CLIENT_ID,
  });

  const authenticationUsecase = new AuthenticationUsecase({
    store,
    history,
    authenticationWebApi,
    errorPresenter,
    apiErrorHandler,
    authUserWebApi,
    organizationWebApiId,
  });

  const createChartsUsecase = new CreateChartsUsecase({
    store,
    salesWebApi,
    segmentWebApi,
    summariesWebApi,
    visitorWebApi,
    apiErrorHandler,
  });

  const editGlobalChartSettingUsecase = new EditGlobalChartSettingUsecase({
    store,
  });

  const organizationUsecase = new OrganizationUsecase({
    store,
    history,
    organizationWebApi,
    apiErrorHandler,
  });

  const createTableUsecase = new CreateTableUsecase({
    store,
  });

  const liftReportUsecase = new LiftReportUsecase({
    store,
    history,
    liftReportWebApi,
    apiErrorHandler,
    messageDispatcher,
  });

  const previewCommentUsecase = new PreviewCommentUsecase({
    previewCommentWebApi,
    apiErrorHandler,
    store,
  });

  const javaScirptCodeUsecase = new JavaScriptCodeUsecase({
    store,
    javaScriptCodeWebApi,
    apiErrorHandler,
  });

  const customReportUsecase = new CustomReportUsecase({
    store,
    history,
    customReportWebApi,
    apiErrorHandler,
  });

  const dependency = {
    errorReporter,
    errorPresenter,
    messageDispatcher,
    authenticationUsecase,
    createChartsUsecase,
    editGlobalChartSettingUsecase,
    organizationUsecase,
    createTableUsecase,
    liftReportUsecase,
    customReportUsecase,
    performanceMeasure,
    previewCommentUsecase,
    javaScirptCodeUsecase,
  };

  const container = document.getElementById('app');
  let isRendered = false;

  const update = (state: State) => {
    const element = createElement(Root, { dependency, history, state, config, apolloClient, googleOauthService });

    if (!isRendered) {
      isRendered = true;

      render(element, container);

      return;
    }

    hydrate(element, container);
  };

  history.listen(({ location }) => {
    googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);
    store.dispatch(setLocation(location));
  });

  store.addMiddleware(createKukerEmitterMiddleware());

  store.subscribe(() => update(store.getState()));

  update(store.getState());

  googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);
};

window.document.addEventListener('DOMContentLoaded', main);
